import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import { senhaInicial } from './format';
import { Lock, ShieldCheck, AlertCircle, Loader2, ArrowRight, LogOut } from 'lucide-react';
import LogoMarcaPortal from './marca/LogoMarcaPortal';

// Portal do Consumidor — troca de senha obrigatória do primeiro acesso
// (rota /portal-consumidor/nova-senha). Toda conta nasce com
// consumidores.senha_provisoria_pendente = true (senha inicial = 6 primeiros
// dígitos do CPF); aqui o consumidor define a senha definitiva e a coluna é
// atualizada para false. A nova senha não pode ser a inicial de novo.

const PortalConsumidorNovaSenha = () => {
    const { user, profile, loading, signOut } = useAuth();
    const navigate = useNavigate();

    const [novaSenha, setNovaSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    if (!user) return <Navigate to="/portal-consumidor" replace />;
    if (profile && profile.role !== 'consumidor') return <Navigate to="/" replace />;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (novaSenha.length < 6) {
            setError('A nova senha deve ter pelo menos 6 caracteres.');
            return;
        }
        if (profile?.cpf_cnpj && novaSenha === senhaInicial(profile.cpf_cnpj)) {
            setError('A nova senha não pode ser os 6 primeiros dígitos do seu CPF. Escolha uma senha diferente.');
            return;
        }
        if (novaSenha !== confirmarSenha) {
            setError('As senhas não conferem.');
            return;
        }

        setSubmitting(true);
        try {
            const { error } = await supabase.auth.updateUser({ password: novaSenha });
            if (error) {
                setError(error.message || 'Erro ao alterar a senha. Tente novamente.');
                return;
            }
            if (user) {
                const { error: consumidorError } = await supabase
                    .from('consumidores')
                    .update({ senha_provisoria_pendente: false })
                    .eq('id', user.id);
                if (consumidorError) console.error('Erro ao atualizar senha_provisoria_pendente:', consumidorError);
            }
            navigate('/portal-consumidor/inicio', { replace: true });
        } catch {
            setError('Ocorreu um erro inesperado. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="p-8">
                    <div className="flex justify-center mb-6"><LogoMarcaPortal variante="cartao" /></div>

                    <div className="text-center mb-8">
                        <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                            <ShieldCheck size={26} className="text-primary" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900">Crie sua nova senha</h1>
                        <p className="text-sm text-gray-500 mt-2">
                            Por segurança, no primeiro acesso você precisa trocar a senha inicial por uma de sua preferência.
                        </p>
                    </div>

                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 text-sm animate-fade-in">
                            <AlertCircle size={18} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nova senha</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={novaSenha}
                                    onChange={(e) => setNovaSenha(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                    placeholder="Mínimo 6 caracteres"
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirmar nova senha</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={confirmarSenha}
                                    onChange={(e) => setConfirmarSenha(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                    placeholder="Repita a nova senha"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl shadow-lg shadow-primary/25 text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-70 disabled:cursor-not-allowed transition-all transform active:scale-[0.98]"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    Salvar e entrar no portal
                                    <ArrowRight size={18} />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 text-center">
                    <button
                        onClick={signOut}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-red-600 transition-colors"
                    >
                        <LogOut size={13} />
                        Sair e continuar depois
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PortalConsumidorNovaSenha;
